import {
  DownloadOutlined,
  ExportOutlined,
  PaperClipOutlined
} from "@ant-design/icons"
import { App, Button } from "antd"
import React, { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { request } from "../../../services/api"
import { saveBlob } from "../../../utils/download-file.ts"
import { attachmentDownloadPath, formatBytes } from "../api/attachments.ts"
import { MessageReadingPane } from "../messages/MessageReadingPane.tsx"
import type { MessageDetail } from "../types/messages.types.ts"

export interface SentAttachment {
  name: string
  size: number | null
  content_type: string
  /** Present only when the reader may download it. */
  id: string | null
}

/** What the detail adds for a sender: copies and files. Older servers send none of them. */
interface MessageCopies {
  cc?: string[]
  bcc?: string[] | null
  attachments?: SentAttachment[]
}

const AttachmentRow = ({ file }: { file: SentAttachment }) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [busy, setBusy] = useState(false)

  const download = async () => {
    if (!file.id) return
    setBusy(true)
    const response = await request.api.get<Blob>(
      attachmentDownloadPath(file.id),
      {},
      { responseType: "blob" }
    )
    setBusy(false)
    if (!response.ok || !response.data) {
      message.error(t("recordComm.pane.downloadFailed", { name: file.name }))
      return
    }
    saveBlob(response.data, file.name)
  }

  return (
    <li className="flex items-center gap-3 rounded-lg border border-solid border-slate-200 px-3 py-2">
      <PaperClipOutlined aria-hidden className="text-slate-500" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-slate-900">
          {file.name}
        </span>
        {file.size !== null && (
          <span className="text-xs text-slate-500">
            {formatBytes(file.size)}
          </span>
        )}
      </span>
      {file.id ? (
        <Button
          size="small"
          icon={<DownloadOutlined aria-hidden />}
          loading={busy}
          aria-label={t("recordComm.pane.download", { name: file.name })}
          onClick={() => void download()}
        />
      ) : (
        <span className="text-xs text-slate-500">
          {t("recordComm.pane.noDownload")}
        </span>
      )}
    </li>
  )
}

const Copies = ({ detail }: { detail: MessageDetail & MessageCopies }) => {
  const { t } = useTranslation()
  const cc = detail.cc ?? []
  const bcc = detail.bcc ?? []
  if (!cc.length && !bcc.length) return null
  return (
    <dl className="m-0 -mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
      {cc.length > 0 && (
        <>
          <dt className="text-slate-500">{t("recordComm.pane.cc")}</dt>
          <dd className="m-0 break-all">{cc.join(", ")}</dd>
        </>
      )}
      {bcc.length > 0 && (
        <>
          <dt className="text-slate-500">{t("recordComm.pane.bcc")}</dt>
          <dd className="m-0 break-all">{bcc.join(", ")}</dd>
        </>
      )}
    </dl>
  )
}

const Attachments = ({ files }: { files: SentAttachment[] }) => {
  const { t } = useTranslation()
  if (!files.length) return null
  return (
    <section aria-labelledby="pane-attachments">
      <h3
        id="pane-attachments"
        className="m-0 mb-2 text-sm font-semibold text-slate-900"
      >
        {t("recordComm.pane.attachments", { count: files.length })}
      </h3>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {files.map((file, index) => (
          <AttachmentRow key={`${file.name}-${index}`} file={file} />
        ))}
      </ul>
    </section>
  )
}

/**
 * The hub's reading pane for one message, with its copies and attachments
 * and a way into the Communication center on the same message.
 */
export const RecordMessagePane = ({
  messageId,
  onNewMessage,
  onQueueAction
}: {
  messageId: string
  onNewMessage?: (message: MessageDetail) => void
  onQueueAction?: (message: MessageDetail) => void
}) => {
  const { t } = useTranslation()
  const [detail, setDetail] = useState<(MessageDetail & MessageCopies) | null>(
    null
  )
  const onLoaded = useCallback(
    (loaded: MessageDetail) =>
      setDetail(loaded as MessageDetail & MessageCopies),
    []
  )

  return (
    <div className="flex flex-col gap-4 [&_:is(h1,h2,h3)]:text-left">
      <Link
        to={`${RoutesPaths.communication.messages}/${messageId}`}
        className="inline-flex items-center gap-1.5 self-end text-sm"
      >
        <ExportOutlined aria-hidden />
        {t("recordComm.pane.openInCenter")}
      </Link>
      <MessageReadingPane
        key={messageId}
        messageId={messageId}
        onLoaded={onLoaded}
        onNewMessage={onNewMessage}
        onQueueAction={onQueueAction}
        afterRecipients={
          detail?.id === messageId ? <Copies detail={detail} /> : null
        }
        afterBody={
          detail?.id === messageId ? (
            <Attachments files={detail.attachments ?? []} />
          ) : null
        }
      />
    </div>
  )
}
